import { useState, useCallback, useEffect } from 'react';

/**
 * Hook for managing hints
 */
export function useHints(hints = []) {
  const [revealedHints, setRevealedHints] = useState([]);
  const [hintsUsedCount, setHintsUsedCount] = useState(0);

  // Reset hints when the hints array changes (new question)
  useEffect(() => {
    setRevealedHints([]);
    setHintsUsedCount(0);
  }, [hints]);

  const revealNextHint = useCallback(() => {
    if (revealedHints.length < hints.length) {
      const nextIndex = revealedHints.length;
      setRevealedHints(prev => [...prev, hints[nextIndex]]);
      setHintsUsedCount(prev => prev + 1);
      return hints[nextIndex];
    }
    return null;
  }, [hints, revealedHints]);

  const resetHints = useCallback(() => {
    setRevealedHints([]);
    setHintsUsedCount(0);
  }, []);

  const hasMoreHints = revealedHints.length < hints.length;

  return {
    revealedHints,
    hintsUsedCount,
    hasMoreHints,
    revealNextHint,
    resetHints,
  };
}

/**
 * Hook for managing user input state
 */
export function useQuestionInput(initialValue = '', questionId = null) {
  const [value, setValue] = useState(initialValue);
  const [isDirty, setIsDirty] = useState(false);

  // Reset when question changes
  useEffect(() => {
    setValue(initialValue);
    setIsDirty(false);
  }, [questionId, initialValue]);

  const handleChange = useCallback((newValue) => {
    setValue(newValue);
    setIsDirty(true);
  }, []);

  const reset = useCallback(() => {
    setValue(initialValue);
    setIsDirty(false);
  }, [initialValue]);

  return {
    value,
    isDirty,
    handleChange,
    reset,
  };
}

/**
 * Hook for managing feedback display
 */
export function useFeedback() {
  const [feedback, setFeedback] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  const showFeedback = useCallback((isCorrect, explanation = '') => {
    setFeedback({ isCorrect, explanation });
    setIsVisible(true);
  }, []);

  const hideFeedback = useCallback(() => {
    setIsVisible(false);
  }, []);

  const clearFeedback = useCallback(() => {
    setFeedback(null);
    setIsVisible(false);
  }, []);

  return {
    feedback,
    isVisible,
    showFeedback,
    hideFeedback,
    clearFeedback,
  };
}
